// client/src/components/layout/Navbar.js
import { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth.js';
import { useSettings } from '../../hooks/useSettings.js';
import { useTheme } from '../../hooks/useTheme.js';
import api from '../../services/api.js';

/* ─── Theme Toggle Button ──────────────────────────── */
function ThemeToggle() {
  const { theme, toggleTheme } = useTheme();
  const isDark = theme === 'dark';

  return (
    <button
      onClick={toggleTheme}
      className="p-2 rounded-lg text-royal-400 dark:text-royal-300 hover:text-gold dark:hover:text-gold hover:bg-royal-100 dark:hover:bg-royal-800/50 transition-colors"
      aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
      title={isDark ? 'Light mode' : 'Dark mode'}
    >
      {isDark ? (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
          <circle cx="12" cy="12" r="5" />
          <path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42" />
        </svg>
      ) : (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
          <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
        </svg>
      )}
    </button>
  );
}

/* ─── Trial Banner ─────────────────────────────────── */
function TrialBanner({ expiresAt }) {
  const now = new Date();
  const expires = new Date(expiresAt);
  const diffMs = expires - now;
  if (diffMs <= 0) return null;

  const daysLeft = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

  return (
    <div className="bg-gold/10 border-b border-gold/20">
      <div className="max-w-7xl mx-auto px-3 sm:px-6 py-2 flex items-center justify-center gap-2 sm:gap-3 text-sm">
        <span className="text-gold font-medium">
          ✨ Free trial — {daysLeft} day{daysLeft !== 1 ? 's' : ''} left
        </span>
        <Link to="/pricing" className="text-royal-700 dark:text-white hover:text-gold-light underline transition-colors">
          Subscribe to keep your features
        </Link>
      </div>
    </div>
  );
}

/* ─── Main Navbar ──────────────────────────────────── */
export default function Navbar() {
  const { user, logout } = useAuth();
  const { settings } = useSettings();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [giftsMenuOpen, setGiftsMenuOpen] = useState(false);
  const userMenuRef = useRef(null);
  const giftsMenuRef = useRef(null);
  const [pendingCancel, setPendingCancel] = useState(false);

  const cardsName = settings.cards_display_name || 'Moonbeams';
  const storiesName = settings.stories_display_name || 'Stories';

  const isOnTrial = user?.trial_expires_at && new Date(user.trial_expires_at) > new Date() && !user.stripecustomerid;

  // logout() now performs a full reset (clears state + token and hard-navigates
  // to home), so there's nothing to do here afterward — a separate navigate()
  // would just race the reload. Keep this thin wrapper so the onClick handlers
  // below read clearly.
  const handleLogout = () => {
    logout();
  };

  useEffect(() => {
    const checkSubscription = async () => {
      if (user?.tier?.ispaid) {
        try {
          const data = await api.get('/subscriptions/current');
          setPendingCancel(data.subscription?.cancelatperiodend === true);
        } catch (err) {}
      } else {
        setPendingCancel(false);
      }
    };
    checkSubscription();
  }, [user]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) setUserMenuOpen(false);
      if (giftsMenuRef.current && !giftsMenuRef.current.contains(event.target)) setGiftsMenuOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  /* ── Reusable class strings ─────────────────────── */
  const linkClass = 'text-royal-800 dark:text-royal-50 hover:text-gold transition-colors font-body';
  const dropdownBg = 'bg-white dark:bg-royal-800 border border-royal-200 dark:border-royal-600 rounded-lg shadow-xl';
  const dropdownItem = 'block px-4 py-2.5 text-base text-royal-800 dark:text-white hover:text-gold hover:bg-cream dark:hover:bg-royal-700';
  const mobileLink = 'block py-3 px-3 text-base text-royal-800 dark:text-royal-50 hover:text-gold hover:bg-cream dark:hover:bg-royal-800/40 rounded-lg transition-colors';

  return (
    <>
      <nav className="bg-cream dark:bg-royal-900 border-b border-royal-200/50 dark:border-royal-700 sticky top-0 z-40 transition-colors duration-300">
        <div className="max-w-7xl mx-auto px-3 sm:px-6">
          <div className="flex items-center justify-between h-14 sm:h-16">

            {/* Logo — royal blue pill in light mode, gold text in dark mode */}
            <Link
              to="/"
              className="font-display text-lg sm:text-2xl flex-shrink-0 transition-colors
                bg-royal-900 dark:bg-transparent text-gold px-4 py-1.5 sm:px-5 sm:py-2 rounded-lg dark:px-0 dark:py-0 dark:rounded-none"
            >
              {settings.site_name || 'Fairytale'}
            </Link>

            {/* Desktop Nav */}
            <div className="hidden lg:flex items-center gap-6">
              <Link to="/" className={linkClass}>Home</Link>

              <div className="relative group">
                <Link to="/moonbeams" className={`${linkClass} py-4 block`}>
                  {cardsName}
                </Link>
                <div className="absolute top-full left-1/2 -translate-x-1/2 pt-0 opacity-0 group-hover:opacity-100 pointer-events-none group-hover:pointer-events-auto transition-opacity z-50">
                  <div className="px-3 py-2 bg-white dark:bg-royal-800 border border-royal-200 dark:border-royal-600 rounded-lg text-sm text-royal-500 dark:text-royal-200 whitespace-nowrap shadow-lg">
                    Digital Cards to Share with Friends
                  </div>
                </div>
              </div>

              <Link to="/stories" className={linkClass}>
                {storiesName}
              </Link>

              <div className="relative" ref={giftsMenuRef}>
                <button
                  onClick={() => setGiftsMenuOpen(!giftsMenuOpen)}
                  className={`flex items-center gap-1 ${linkClass} py-4`}
                >
                  Gifts
                  <svg
                    className={`w-4 h-4 transition-transform ${giftsMenuOpen ? 'rotate-180' : ''}`}
                    fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {giftsMenuOpen && (
                  <div className="absolute top-full left-1/2 -translate-x-1/2 pt-1 w-56 z-50">
                    <div className={dropdownBg}>
                      <Link
                        to="/gift"
                        className={`${dropdownItem} rounded-t-lg`}
                        onClick={() => setGiftsMenuOpen(false)}
                      >
                        🎁 Gift a Subscription
                      </Link>
                      <Link
                        to="/gift/accept"
                        className={`${dropdownItem} rounded-b-lg`}
                        onClick={() => setGiftsMenuOpen(false)}
                      >
                        🎉 Accept a Gift
                      </Link>
                    </div>
                  </div>
                )}
              </div>

              <Link to="/about" className={linkClass}>About Us</Link>
              <Link to="/pricing" className={linkClass}>Pricing</Link>

              {/* Pending Cancellation Warning */}
              {pendingCancel && (
                <Link
                  to="/account"
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-100 dark:bg-amber-900/40 border border-amber-400 dark:border-amber-600/50 rounded-full text-amber-700 dark:text-amber-300 text-sm hover:bg-amber-200 dark:hover:bg-amber-900/60 transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  <span className="hidden xl:inline">Subscription ending</span>
                </Link>
              )}

              {/* Theme Toggle */}
              <ThemeToggle />

              {user ? (
                <div className="relative" ref={userMenuRef}>
                  <button
                    onClick={() => setUserMenuOpen(!userMenuOpen)}
                    className={`flex items-center gap-1.5 ${linkClass} py-2`}
                  >
                    {user.firstname || 'Account'}
                    <svg
                      className={`w-4 h-4 transition-transform ${userMenuOpen ? 'rotate-180' : ''}`}
                      fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  {userMenuOpen && (
                    <div className="absolute top-full right-0 pt-1 w-48 z-50">
                      <div className={dropdownBg}>
                        {/* Dashboard — featured first item */}
                        <Link
                          to="/dashboard"
                          className={`${dropdownItem} rounded-t-lg !text-gold font-semibold`}
                          onClick={() => setUserMenuOpen(false)}
                        >
                          ✨ Dashboard
                        </Link>
                        <div className="border-t border-royal-200 dark:border-royal-700/50" />
                        <Link
                          to="/my-moonbeams"
                          className={dropdownItem}
                          onClick={() => setUserMenuOpen(false)}
                        >
                          My {cardsName}
                        </Link>
                        <Link to="/contacts" className={dropdownItem} onClick={() => setUserMenuOpen(false)}>
                          Contacts
                        </Link>
                        <Link to="/gift/my-gifts" className={dropdownItem} onClick={() => setUserMenuOpen(false)}>
                          My Gifts
                        </Link>
                        {user.usertype >= 4 && (
                          <Link to="/account/business-profile" className={dropdownItem} onClick={() => setUserMenuOpen(false)}>
                            Business Profile
                          </Link>
                        )}
                        <Link to="/account" className={dropdownItem} onClick={() => setUserMenuOpen(false)}>
                          Account
                          {pendingCancel && (
                            <span className="ml-2 inline-block w-2 h-2 bg-amber-400 rounded-full"></span>
                          )}
                        </Link>
                        {user.usertype >= 4 && (
                          <Link to="/account/analytics" className={dropdownItem} onClick={() => setUserMenuOpen(false)}>
                            Analytics
                          </Link>
                        )}
                        {user.isadmin && (
                          <Link
                            to="/admin"
                            className={`${dropdownItem} !text-gold`}
                            onClick={() => setUserMenuOpen(false)}
                          >
                            Admin
                          </Link>
                        )}
                        <button
                          onClick={() => { handleLogout(); setUserMenuOpen(false); }}
                          className="block w-full text-left px-4 py-2.5 text-base text-red-500 dark:text-red-400 hover:text-red-400 dark:hover:text-red-300 hover:bg-cream dark:hover:bg-royal-700 rounded-b-lg"
                        >
                          Logout
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <Link to="/login" className="px-6 py-2 bg-royal-900 dark:bg-gold text-gold dark:text-royal-900 font-semibold text-sm rounded-lg hover:bg-royal-800 dark:hover:bg-gold-light transition-colors">
                  Sign In
                </Link>
              )}
            </div>

            {/* Mobile: theme toggle + hamburger */}
            <div className="lg:hidden flex items-center gap-1">
              <ThemeToggle />
              <button className="text-royal-800 dark:text-royal-50 p-2 -mr-1" onClick={() => setMenuOpen(!menuOpen)}>
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  {menuOpen ? (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  ) : (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  )}
                </svg>
              </button>
            </div>
          </div>

          {/* Mobile Nav */}
          {menuOpen && (
            <div className="lg:hidden pb-4 space-y-1 border-t border-royal-200/30 dark:border-royal-700/50 pt-3">
              <Link to="/" className={mobileLink} onClick={() => setMenuOpen(false)}>Home</Link>
              <Link to="/moonbeams" className={mobileLink} onClick={() => setMenuOpen(false)}>{cardsName}</Link>
              <Link to="/stories" className={mobileLink} onClick={() => setMenuOpen(false)}>{storiesName}</Link>
              <Link to="/gift" className={mobileLink} onClick={() => setMenuOpen(false)}>🎁 Gift a Subscription</Link>
              <Link to="/gift/accept" className={mobileLink} onClick={() => setMenuOpen(false)}>🎉 Accept a Gift</Link>
              <Link to="/about" className={mobileLink} onClick={() => setMenuOpen(false)}>About Us</Link>
              <Link to="/pricing" className={mobileLink} onClick={() => setMenuOpen(false)}>Pricing</Link>

              {pendingCancel && (
                <Link
                  to="/account"
                  className="flex items-center gap-2 py-3 px-3 text-amber-700 dark:text-amber-300 hover:bg-cream dark:hover:bg-royal-800/40 rounded-lg transition-colors"
                  onClick={() => setMenuOpen(false)}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  Subscription ending soon
                </Link>
              )}

              <div className="border-t border-royal-200/30 dark:border-royal-700/50 my-2"></div>

              {user ? (
                <>
                  <p className="px-3 py-1.5 text-sm text-royal-500 dark:text-royal-200">Signed in as <span className="text-gold">{user.firstname || user.email}</span></p>
                  {/* Dashboard — featured first item */}
                  <Link
                    to="/dashboard"
                    className="block py-3 px-3 text-base text-gold font-semibold hover:bg-cream dark:hover:bg-royal-800/40 rounded-lg transition-colors"
                    onClick={() => setMenuOpen(false)}
                  >
                    ✨ Dashboard
                  </Link>
                  <div className="border-t border-royal-200/30 dark:border-royal-700/50 my-1"></div>
                  <Link to="/my-moonbeams" className={mobileLink} onClick={() => setMenuOpen(false)}>My {cardsName}</Link>
                  <Link to="/contacts" className={mobileLink} onClick={() => setMenuOpen(false)}>Contacts</Link>
                  <Link to="/gift/my-gifts" className={mobileLink} onClick={() => setMenuOpen(false)}>My Gifts</Link>
                  {user.usertype >= 4 && (
                    <Link to="/account/business-profile" className={mobileLink} onClick={() => setMenuOpen(false)}>Business Profile</Link>
                  )}
                  <Link to="/account" className={mobileLink} onClick={() => setMenuOpen(false)}>
                    Account
                    {pendingCancel && <span className="ml-2 inline-block w-2 h-2 bg-amber-400 rounded-full"></span>}
                  </Link>
                  {user.usertype >= 4 && (
                    <Link to="/account/analytics" className={mobileLink} onClick={() => setMenuOpen(false)}>Analytics</Link>
                  )}
                  {user.isadmin && (
                    <Link to="/admin" className="block py-3 px-3 text-base text-gold hover:bg-cream dark:hover:bg-royal-800/40 rounded-lg transition-colors" onClick={() => setMenuOpen(false)}>Admin</Link>
                  )}
                  <button onClick={() => { handleLogout(); setMenuOpen(false); }} className="block w-full text-left py-3 px-3 text-base text-red-500 dark:text-red-400 hover:bg-cream dark:hover:bg-royal-800/40 rounded-lg transition-colors">Logout</button>
                </>
              ) : (
                <Link to="/login" className="block py-3 px-3 text-base text-gold font-semibold hover:bg-cream dark:hover:bg-royal-800/40 rounded-lg transition-colors" onClick={() => setMenuOpen(false)}>Sign In</Link>
              )}
            </div>
          )}
        </div>
      </nav>

      {isOnTrial && <TrialBanner expiresAt={user.trial_expires_at} />}
    </>
  );
}